import {
  callApiGet,
  callApiPost,
  callApiPatch,
  formatParams,
  createParams,
  constructApiUrl,
  validateParams,
  getChangedParams,
  getPolicyID,
  getRequestPreparePurchase,
} from './common';
import { createErrorBody } from '../error';
import { getPackagesWithCoverage } from '../package/packageUtils';
import {
  createCar,
  updateCar,
  prepareCar,
  validatedCarRegistrationNumber,
  validateCarRegistrationNumber,
} from '../../actions/Api';

const type = 'car';

// Create map for key values in th API.
const carKeyMap = [
  {
    name: 'model',
    mappedName: 's',
  },
  {
    name: 'modelYear',
    mappedName: 'year',
  },
];

/**
 * Determines if the state allows us to run updates.
 * @param  {object} state The current state.
 * @return {boolean}      If we can update or not.
 */
const shouldUpdate = (state) => {
  return state.isUpdatingCar ? state.isUpdatingCar : true;
};

/**
 * Call the API using GET.
 * @param  {string}   service  The service to call.
 * @param  {Object}   params   The params for the service.
 * @param  {Function} dispatch The dispatch function.
 * @param  {boolean} externalService
 * @param  {boolean} useToken
 * @return {Promise}           The Promise.
 */
const callCarApiGet = (service, params, dispatch, externalService = false, useToken = true) => {
  const url = constructApiUrl(type, service);
  return callApiGet(url, params, dispatch, externalService, useToken);
};

/**
 * Call the API using POST.
 * @param  {string}   service  The service to call.
 * @param  {Object}   params   The params for the service.
 * @param  {Function} dispatch The dispatch function.
 * @return {Promise}           The Promise.
 */
const callCarApiPost = (service, params, dispatch) => {
  const url = constructApiUrl(type, service);
  return callApiPost(url, params, dispatch);
};

/**
 * Call the API using PATCH.
 * @param  {string}   service  The service to call.
 * @param  {Object}   params   The params for the service.
 * @param  {Function} dispatch The dispatch function.
 * @return {Promise}           The Promise.
 */
const callCarApiPatch = (service, params, dispatch) => {
  const url = constructApiUrl(type, service);
  return callApiPatch(url, params, dispatch);
};

/**
 * Call the getVehicleModels service.
 * @param  {Object} params  The params to call the service with.
 * @return {Promise}        The promise of the service.
 */
export const getVehicleModels = (params, dispatch) => {
  const service = 'getVehicleModels';
  const mappedParams = formatParams(params, carKeyMap);
  return callCarApiGet(service, mappedParams, dispatch).then(response => {
    return Object.keys(response).reduce((result, key) => {
      return [
        ...result,
        response[key],
      ];
    }, []);
  });
};

/**
 * Get excess amount for car.
 *
 * @param  {Object} formValues
 * @param  {function} dispatch
 * @return {Promise}
 */
export const getCarExcessAmount = (formValues, dispatch) => {
  const { user: { customerID } } = formValues;
  const policyID = getPolicyID(formValues, type, true);
  const service = `getExcessAmount/${customerID}/${policyID}`;
  return callCarApiGet(service, {}, dispatch, false, false);
};

/**
 * Dispatches the create action.
 * @param  {string} service The service to use.
 * @param  {Object} parameters The params to call the service with.
 * @return {function} Dispatch function.
 */
const callCreateCar = (service, parameters) => {
  const params = parameters;
  let id = null;
  if (Object.hasOwnProperty.call(params, 'packageId')) {
    const { packageId } = params;
    id = packageId;
    delete params.packageId;
  }
  return dispatch => {
    return callCarApiPost(service, params, dispatch).then(data => {
      const newData = data;
      if (id) {
        newData.packageId = id;
      }
      return newData;
    });
  };
};

/**
 * Call the getVehicleModelsByRegistrationNumber service.
 * @param  {Object} params  The params to call the service with.
 * @return {Promise}        The promise of the service.
 */
export const requestVehicleModelsByRegistrationNumber = (params) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const service = 'getVehicleModelsByRegistrationNumber';
      dispatch(validateCarRegistrationNumber(params));
      return callCarApiGet(service, params, dispatch).then(response => {
        // Let the calling code know there's nothing to wait for.
        const modelVariant = Object.keys(response).reduce((result, key) => {
          if (response[key].vehicleMakeId) {
            return [
              ...result,
              {
                key: response[key].vehicleMakeId,
                value: response[key].variantName,
              },
            ];
          }
          return result;
        }, []);
        const hasData = modelVariant.length > 0;
        const defaultModel = response[0];
        defaultModel.vehicleMakeIdRegNo = defaultModel.vehicleMakeId;
        delete defaultModel.variantSpecialText;
        delete defaultModel.vehicleMakeId;
        if (hasData) {
          return {
            ...defaultModel,
            registrationNumberValid: hasData,
            registrationNumberNoService: false,
            registrationNumberProvided: hasData,
            registrationNumber: params.registrationNumber,
            modelVariant,
            showModelVariant: modelVariant.length > 1,
          };
        }
        return {
          registrationNumberValid: hasData,
          registrationNumberNoService: false,
          vehicleMakeIdRegNo: '',
          registrationNumberProvided: hasData,
        };
      });
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * return the mapping from previous request.
 * @param  {Object} data  The params to call the service with.
 * @return {Promise}        The promise of the service.
 */
export const getVehicleModelsByRegistrationNumber = (data) => {
  return dispatch => {
    return dispatch(validatedCarRegistrationNumber(data));
  };
};

/**
 * Call the create service.
 * @param  {Object} params The params to call the service with.
 * @param  {Object} formValues
 * @return {Promise}     The promise of the service.
 */
export const requestCreateCar = (params, formValues = {}) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const state = getState();
      // Map data from Ajax if the data is not entered manually.
      const newFormValues = formValues;
      newFormValues.vehicleMakeId =
        state.userData.registrationNumberValid ?
          state.userData.vehicleMakeIdRegNo :
          formValues.vehicleMakeId;
      const mappedParams = params ? createParams(params, newFormValues, getState()) : {};
      // Ensure that we have the correct paramters. Fail if not.
      const errors = params ? validateParams(mappedParams, params) : [];
      if (errors.length > 0) {
        return Promise.reject(createErrorBody('Missing parameters', errors));
      }
      const service = '';
      const { registrationNumberProvided } = params;
      const serviceCalls = [];
      const packageInfo = getPackagesWithCoverage(state);

      dispatch(createCar({ registrationNumberProvided }));
      if (packageInfo.length > 0) {
        if (state.userData.subProduct && state.userData.subProduct.includes('trygdrive')) {
          mappedParams.trygDrive = true;
        }
        packageInfo.forEach(item => {
          const packageDetails = item;
          packageDetails.coverageCodes =
            state.userData[packageDetails.packageId]
            && state.userData[packageDetails.packageId].coverageCodes
              ? state.userData[packageDetails.packageId].coverageCodes
              : packageDetails.coverageCodes;

          packageDetails.removedCoverages = state.userData[packageDetails.packageId]
          && state.userData[packageDetails.packageId].coverageCodes
            ? []
            : packageDetails.removedCoverages;

          serviceCalls.push(dispatch(callCreateCar(service, {
            ...mappedParams, ...packageDetails,
          })));
        });
        return Promise.all(serviceCalls).then(data => {
          let response = {};
          if (data.length > 1) {
            data.forEach(element => {
              response[element.packageId] = element;
            });
          } else if (data.length === 1) {
            [response] = data;
          }
          return response;
        });
      }

      return dispatch(callCreateCar(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the update action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callUpdateCar = (service, params) => {
  return dispatch => {
    const { registrationNumberProvided } = params;
    dispatch(updateCar({ registrationNumberProvided }));
    return callCarApiPatch(service, params, dispatch);
  };
};

/**
 * Call the update service.
 * @param  {array}  params     The params to call the service with.
 * @param  {Object} formValues Values from the form that might not yet be set in the state.
 * @return {Promise}           The promise of the service.
 */
export const requestUpdateCar = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const state = getState();
      // Map data from Ajax if the data is not entered manually.
      const newFormValues = formValues;
      newFormValues.vehicleMakeId =
        state.userData.registrationNumberValid ?
          state.userData.vehicleMakeIdRegNo :
          formValues.vehicleMakeId;
      let mappedParams = params ? createParams(params, newFormValues, getState()) : {};
      const policyID = getPolicyID(state.userData, type);
      if (state.userData.car) {
        // Find the changed values and pass those along.
        const additionalParams = { policyID };
        mappedParams = getChangedParams(mappedParams, state.userData.car.usedParams, ['policyID'], additionalParams);
        // If theres only policyID in the params we just resolve. It means no update is needed.
        if (Object.keys(mappedParams).length <= 1) {
          return Promise.resolve();
        }
      }
      const service = `${policyID}`;
      return dispatch(callUpdateCar(service, mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};

/**
 * Dispatches the prepare action.
 * @param  {string} service The service to use.
 * @param  {Object} params The params to call the service with.
 * @return {function} Dispatch function.
 */
const callPrepareCar = (service, params) => {
  return dispatch => {
    dispatch(prepareCar());
    return callCarApiPatch(service, params, dispatch);
  };
};

/**
 * Call the prepare purchase service.
 * @param  {array}  params     The params to call the service with.
 * @param  {Object} formValues Values from the form that might not yet be set in the state.
 * @return {Promise}     The promise of the service.
 */
export const requestPrepareCar = (params, formValues) => {
  return (dispatch, getState) => {
    if (shouldUpdate(getState())) {
      const prepare = getRequestPreparePurchase(type, getState(), formValues, params);
      return dispatch(callPrepareCar(prepare.service, prepare.mappedParams));
    }

    // Let the calling code know there's nothing to wait for.
    return Promise.resolve();
  };
};



// WEBPACK FOOTER //
// ./src/utilities/api/car.js